import {
  createTransportClient,
  getTransportTokenForOrganization,
} from "@/transport"

import type { ApplicationAccessResponse } from "@/types_api/backend/transport/application_access_response"

export interface AdmissionScope {
  userId: string
  organizationId: string | null
  organizationSlug: string | null
}

type GetToken = (options?: {
  organizationId?: string
  skipCache?: boolean
}) => Promise<string | null>

function tokenMatchesScope(token: string | null, scope: AdmissionScope) {
  if (!token) {
    return false
  }
  try {
    const payload = token.split(".")[1]
    if (!payload) {
      return false
    }
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>
    return (
      claims.sub === scope.userId &&
      (claims.org_id ?? null) === scope.organizationId &&
      (scope.organizationSlug === null ||
        claims.org_slug === scope.organizationSlug)
    )
  } catch {
    return false
  }
}

const MODES = new Set(["normal", "enterprise_only", "existing_users_only"])
const REASONS = new Set([
  "allowed",
  "organization_required",
  "organization_denied",
  "identity_unavailable",
  "account_disabled",
  "existing_user_required",
  "subscription_required",
  "enterprise_required",
])

function decisionMatchesScope(
  decision: ApplicationAccessResponse,
  scope: AdmissionScope,
) {
  return (
    decision !== null &&
    MODES.has(decision.mode) &&
    REASONS.has(decision.reason) &&
    typeof decision.policy_revision === "string" &&
    /^[a-f0-9]{64}$/.test(decision.policy_revision) &&
    decision.clerk_user_id === scope.userId &&
    decision.clerk_organization_id === scope.organizationId &&
    (scope.organizationId === null
      ? decision.organization_id === null
      : typeof decision.organization_id === "string" &&
        decision.organization_id.length > 0) &&
    typeof decision.allowed === "boolean" &&
    typeof decision.user_allowed === "boolean" &&
    typeof decision.signup_allowed === "boolean" &&
    decision.allowed === (decision.reason === "allowed") &&
    (!decision.allowed ||
      (decision.user_allowed && scope.organizationId !== null)) &&
    (!decision.signup_allowed ||
      (decision.mode === "normal" &&
        decision.user_allowed &&
        ["allowed", "organization_required"].includes(decision.reason))) &&
    (decision.reason !== "organization_required" ||
      (scope.organizationId === null && decision.user_allowed)) &&
    (![
      "organization_denied",
      "subscription_required",
      "enterprise_required",
    ].includes(decision.reason) ||
      scope.organizationId !== null) &&
    (![
      "identity_unavailable",
      "account_disabled",
      "existing_user_required",
    ].includes(decision.reason) ||
      !decision.user_allowed) &&
    (decision.reason !== "enterprise_required" ||
      decision.mode === "enterprise_only")
  )
}

/** Reads a fresh decision using an explicit tab scope, including no-org sessions. */
export async function readApplicationAdmission({
  scope,
  getToken,
  signal,
}: {
  scope: AdmissionScope
  getToken: GetToken
  signal: AbortSignal
}): Promise<ApplicationAccessResponse> {
  const token = scope.organizationId
    ? await getTransportTokenForOrganization({
        getToken,
        organizationId: scope.organizationId,
        organizationSlug: scope.organizationSlug,
        requireOrganizationClaims: true,
      })
    : await getToken({ organizationId: "", skipCache: true })
  signal.throwIfAborted()
  // Empty organizationId explicitly requests no org. If Clerk returns an active
  // org token instead, reject it rather than consulting another tab's cookie.
  if (!tokenMatchesScope(token, scope)) {
    throw new Error("Application access identity could not be verified.")
  }
  const client = createTransportClient({
    getToken: () => token,
    getOrganizationContext: () =>
      scope.organizationSlug ? { slug: scope.organizationSlug } : null,
  })
  const decision = await client.operations.getApplicationAccess({ signal })
  signal.throwIfAborted()
  if (!decisionMatchesScope(decision, scope)) {
    throw new Error("Application access response could not be verified.")
  }
  return decision
}
